import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import SiteHeader from "../components/SiteHeader";

export default function Taluks() {
  const navigate = useNavigate();

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Filters
  const [stateFilter, setStateFilter] = useState(""); // state_id
  const [districtFilter, setDistrictFilter] = useState(""); // district_id
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState(""); // "", "1", "0"

  // Form
  const [form, setForm] = useState({
    id: null,
    state_id: "",
    district_id: "",
    name: "",
    is_active: 1,
  });

  const isEditing = form.id !== null;

  const pickItems = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.items)) return data.items;
    return [];
  };

  const loadStates = async () => {
    const { data } = await api.get("/api/admin/states");
    return pickItems(data);
  };

  const loadDistricts = async (state_id) => {
    if (!state_id) return [];
    const { data } = await api.get("/api/admin/districts", { params: { state_id } });
    return pickItems(data);
  };

  const loadTaluks = async (opts = {}) => {
    const q = opts.search !== undefined ? opts.search : search;
    const active = opts.active !== undefined ? opts.active : activeFilter;
    const district_id =
      opts.district_id !== undefined ? opts.district_id : districtFilter;

    const { data } = await api.get("/api/admin/taluks", {
      params: {
        q: q || undefined,
        active: active === "" ? undefined : active,
        district_id: district_id === "" ? undefined : district_id,
      },
    });
    return pickItems(data);
  };

  const initLoad = async () => {
    try {
      setLoading(true);
      setErr("");

      const st = await loadStates();
      setStates(st);

      // Default state -> first active
      let stId = stateFilter;
      if (!stId && st.length) {
        const firstActive = st.find((x) => x.is_active !== 0) || st[0];
        stId = String(firstActive.id);
        setStateFilter(stId);
        setForm((f) => ({ ...f, state_id: stId }));
      }

      const ds = await loadDistricts(stId);
      setDistricts(ds);

      // Default district -> first active
      let dId = districtFilter;
      if (!dId && ds.length) {
        const firstActiveD = ds.find((x) => x.is_active !== 0) || ds[0];
        dId = String(firstActiveD.id);
        setDistrictFilter(dId);
        setForm((f) => ({ ...f, district_id: dId }));
      }

      const list = await loadTaluks({ district_id: dId });
      setItems(list);
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load taluks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedStateName = useMemo(() => {
    const s = states.find((x) => String(x.id) === String(stateFilter));
    return s?.name || "";
  }, [states, stateFilter]);

  const selectedDistrictName = useMemo(() => {
    const d = districts.find((x) => String(x.id) === String(districtFilter));
    return d?.name || "";
  }, [districts, districtFilter]);

  const resetForm = () => {
    setForm((f) => ({
      id: null,
      state_id: stateFilter || f.state_id || "",
      district_id: districtFilter || f.district_id || "",
      name: "",
      is_active: 1,
    }));
  };

  const onStateChange = async (val) => {
    // When state changes: reload districts; reset district selection; reload taluks
    setStateFilter(val);
    setDistrictFilter("");
    setDistricts([]);
    setItems([]);

    setForm((f) => ({
      ...f,
      id: null,
      state_id: val,
      district_id: "",
      name: "",
    }));

    try {
      setLoading(true);
      const ds = await loadDistricts(val);
      setDistricts(ds);

      let newDistrictId = "";
      if (ds.length) {
        const firstActiveD = ds.find((x) => x.is_active !== 0) || ds[0];
        newDistrictId = String(firstActiveD.id);
        setDistrictFilter(newDistrictId);
        setForm((f) => ({ ...f, district_id: newDistrictId }));
        const list = await loadTaluks({ district_id: newDistrictId });
        setItems(list);
      } else {
        setItems([]);
      }
      setErr("");
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load districts/taluks");
    } finally {
      setLoading(false);
    }
  };

  const onDistrictChange = async (val) => {
    setDistrictFilter(val);
    setForm((f) => ({ ...f, id: null, district_id: val, name: "" }));
    try {
      setLoading(true);
      const list = await loadTaluks({ district_id: val });
      setItems(list);
      setErr("");
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load taluks");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.state_id) return alert("State is required");
    if (!form.district_id) return alert("District is required");
    if (!form.name.trim()) return alert("Taluk name is required");

    try {
      setLoading(true);

      const payload = {
        district_id: parseInt(form.district_id, 10),
        name: form.name.trim(),
        is_active: form.is_active ? 1 : 0,
      };

      if (isEditing) {
        await api.put(`/api/admin/taluks/${form.id}`, payload);
      } else {
        await api.post("/api/admin/taluks", payload);
      }

      resetForm();
      const list = await loadTaluks();
      setItems(list);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || "Failed to save taluk");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (item) => {
    // Ensure state/district dropdowns align with edited record
    const stId = String(item.state_id);
    const dId = String(item.district_id);

    setForm({
      id: item.id,
      state_id: stId,
      district_id: dId,
      name: item.name || "",
      is_active: item.is_active === 0 ? 0 : 1,
    });

    if (stateFilter !== stId) {
      setStateFilter(stId);
      try {
        setLoading(true);
        const ds = await loadDistricts(stId);
        setDistricts(ds);
        setDistrictFilter(dId);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    } else {
      setDistrictFilter(dId);
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this taluk?")) return;

    try {
      setLoading(true);
      await api.delete(`/api/admin/taluks/${id}`);
      const list = await loadTaluks();
      setItems(list);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || "Failed to delete taluk");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!districtFilter) {
      alert("Select district to search taluks");
      return;
    }
    try {
      setLoading(true);
      const list = await loadTaluks({
        district_id: districtFilter,
        search,
        active: activeFilter,
      });
      setItems(list);
      setErr("");
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load taluks");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    setSearch("");
    setActiveFilter("");
    await handleSearch();
  };

  const styles = {
    page: { minHeight: "100vh", background: "#f1f5f9" },
    container: { maxWidth: 1020, margin: "0 auto", padding: 16 },
    card: {
      background: "#fff",
      borderRadius: 10,
      padding: 16,
      boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
      marginBottom: 16,
    },
    input: {
      width: "100%",
      padding: "10px 12px",
      borderRadius: 8,
      border: "1px solid #cbd5e1",
      fontSize: 14,
    },
    label: { fontSize: 13, fontWeight: 600, marginBottom: 4, display: "block" },
    btnPrimary: {
      background: "#0f766e",
      color: "#fff",
      border: "none",
      borderRadius: 999,
      padding: "8px 18px",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
    },
    btnGhost: {
      background: "#e2e8f0",
      borderRadius: 999,
      padding: "8px 16px",
      border: "none",
      fontSize: 14,
      cursor: "pointer",
    },
    pill: (active) => ({
      display: "inline-block",
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      background: active ? "#dcfce7" : "#fee2e2",
      color: active ? "#166534" : "#991b1b",
      border: `1px solid ${active ? "#bbf7d0" : "#fecaca"}`,
    }),
    hint: { fontSize: 12, color: "#64748b", marginTop: 8 },
  };

  return (
    <div style={styles.page}>
      <SiteHeader showAdmin />

      <div style={styles.container}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
          <h2 style={{ fontSize: 20, margin: 0 }}>Taluk Master</h2>
          <button onClick={() => navigate("/dashboard")} style={styles.btnGhost}>
            ← Back
          </button>
        </div>

        {/* FORM CARD */}
        <div style={styles.card}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <div>
                <label style={styles.label}>State *</label>
                <select
                  style={styles.input}
                  value={form.state_id}
                  onChange={(e) => {
                    const val = e.target.value;
                    setForm((f) => ({ ...f, state_id: val }));
                    onStateChange(val);
                  }}
                >
                  <option value="">Select state</option>
                  {states.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} {s.is_active === 0 ? "(Inactive)" : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={styles.label}>District *</label>
                <select
                  style={styles.input}
                  value={form.district_id}
                  onChange={(e) => {
                    const val = e.target.value;
                    setForm((f) => ({ ...f, district_id: val }));
                    onDistrictChange(val);
                  }}
                  disabled={!form.state_id}
                >
                  <option value="">Select district</option>
                  {districts.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} {d.is_active === 0 ? "(Inactive)" : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={styles.label}>Taluk Name *</label>
                <input
                  style={styles.input}
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Enter taluk name"
                />
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
              <input
                id="taluk_active"
                type="checkbox"
                checked={!!form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked ? 1 : 0 }))}
              />
              <label htmlFor="taluk_active" style={{ fontSize: 13, color: "#0f172a" }}>
                Active
              </label>
            </div>

            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              <button style={styles.btnPrimary} type="submit" disabled={loading}>
                {isEditing ? "Update Taluk" : "Add Taluk"}
              </button>

              {isEditing && (
                <button type="button" style={styles.btnGhost} onClick={resetForm} disabled={loading}>
                  Cancel Edit
                </button>
              )}
            </div>

            <div style={styles.hint}>
              Taluk depends on District. District depends on State.
            </div>
          </form>
        </div>

        {/* SEARCH + LIST */}
        <div style={styles.card}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <select
              style={{ ...styles.input, maxWidth: 240 }}
              value={stateFilter}
              onChange={(e) => onStateChange(e.target.value)}
              title="Filter by State"
            >
              <option value="">Select state</option>
              {states.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} {s.is_active === 0 ? "(Inactive)" : ""}
                </option>
              ))}
            </select>

            <select
              style={{ ...styles.input, maxWidth: 240 }}
              value={districtFilter}
              onChange={(e) => onDistrictChange(e.target.value)}
              title="Filter by District"
              disabled={!stateFilter}
            >
              <option value="">Select district</option>
              {districts.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} {d.is_active === 0 ? "(Inactive)" : ""}
                </option>
              ))}
            </select>

            <input
              style={{ ...styles.input, maxWidth: 240 }}
              placeholder="Search taluk..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={!districtFilter}
            />

            <select
              style={{ ...styles.input, maxWidth: 170 }}
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              disabled={!districtFilter}
            >
              <option value="">All</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>

            <button style={styles.btnGhost} type="button" onClick={handleSearch} disabled={!districtFilter}>
              Search
            </button>

            <button style={styles.btnGhost} type="button" onClick={handleReset} disabled={!districtFilter}>
              Reset
            </button>
          </div>

          <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>
            Showing taluks {selectedStateName ? `for ${selectedStateName}` : ""}{" "}
            {selectedDistrictName ? ` / ${selectedDistrictName}` : ""}
          </div>

          {loading ? (
            <div style={{ padding: 12 }}>Loading...</div>
          ) : err ? (
            <div style={{ color: "#b91c1c", marginTop: 10 }}>{err}</div>
          ) : !items.length ? (
            <div style={{ padding: 12, color: "#64748b" }}>No taluks found</div>
          ) : (
            <div style={{ marginTop: 12, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <th style={{ padding: 10, textAlign: "left" }}>State</th>
                    <th style={{ padding: 10, textAlign: "left" }}>District</th>
                    <th style={{ padding: 10, textAlign: "left" }}>Taluk</th>
                    <th style={{ padding: 10, textAlign: "left" }}>Status</th>
                    <th style={{ padding: 10, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const active = item.is_active !== 0;
                    return (
                      <tr key={item.id} style={{ borderTop: "1px solid #e2e8f0" }}>
                        <td style={{ padding: 10 }}>{item.state_name || "-"}</td>
                        <td style={{ padding: 10 }}>{item.district_name || "-"}</td>
                        <td style={{ padding: 10 }}>{item.name}</td>
                        <td style={{ padding: 10 }}>
                          <span style={styles.pill(active)}>{active ? "Active" : "Inactive"}</span>
                        </td>
                        <td style={{ padding: 10, textAlign: "right", whiteSpace: "nowrap" }}>
                          <button
                            type="button"
                            onClick={() => handleEdit(item)}
                            style={{ ...styles.btnGhost, padding: "4px 12px", marginRight: 6, fontSize: 13 }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(item.id)}
                            style={{
                              ...styles.btnGhost,
                              padding: "4px 12px",
                              background: "#fee2e2",
                              border: "1px solid #fecaca",
                              fontSize: 13,
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
                Note: Taluk delete is allowed (no child dependency below it).
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
